"use client";

/**
 * Branded customer-portal login. Rendered instead of the marketing login when
 * the request arrives on an org subdomain (`<slug>.portalzugang.de`) and that
 * org has been resolved server-side. Shows the org's logo + brand color and
 * reuses the shared LoginForm (which redirects through /portal-launch on a
 * portal host). The staff/marketing login is left completely untouched.
 */

import {
  pickFormFillOnAccentText,
  resolveFormFillAccent,
} from "@/app/(dashboard)/_components/forms/fill/form-fill-accent";
import type { ReactElement } from "react";
import "./login.css";
import { useNativeAppKind } from "@/lib/native-app/native-app-context";
import { useTranslations } from "next-intl";
import { LoginForm } from "./login-form";
import { AppLegalLinks } from "./login-screen-app";
import { CARD_BG, INK, PAGE_BG } from "./login-shared";
import { PortalBrandLockup } from "./portal-brand-lockup";

const FONT_STACK =
  "var(--font-inter), system-ui, -apple-system, 'Segoe UI', sans-serif";

export type PortalLoginBranding = {
  /** Null on the bare apex / unknown subdomain → neutral „Kundenportal". */
  readonly org_name: string | null;
  readonly logo_url: string | null;
  readonly brand_color: string | null;
};

function hexToRgb(hex: string): { r: number; g: number; b: number } {
  const match = /^#?([0-9a-fA-F]{6})$/.exec(hex.trim());
  const n = match?.[1] ? Number.parseInt(match[1], 16) : 0x008070;
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}

export function PortalLoginScreen({
  branding,
  compact,
}: {
  readonly branding: PortalLoginBranding;
  readonly compact: boolean;
}): ReactElement {
  const t = useTranslations("public.portalLoginScreen");
  const nativeApp = useNativeAppKind();
  const accent = resolveFormFillAccent(branding.brand_color ?? "");
  const accentText = pickFormFillOnAccentText(accent);
  const { r, g, b } = hexToRgb(accent);
  const accentShadow = `rgba(${r}, ${g}, ${b}, 0.5)`;
  const isBrandedOrg = branding.org_name != null;

  return (
    <div
      className="mklogin"
      style={{
        position: "relative",
        minHeight: "100dvh",
        boxSizing: "border-box",
        fontFamily: FONT_STACK,
        background: PAGE_BG,
        color: INK,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: compact ? "32px 16px" : "48px 32px",
        overflowX: "hidden",
      }}
    >
      <div
        style={{
          position: "absolute",
          inset: 0,
          zIndex: 0,
          pointerEvents: "none",
          backgroundImage: `radial-gradient(60% 50% at 12% 0%, rgba(${r},${g},${b},0.12) 0%, transparent 60%), radial-gradient(54% 56% at 100% 100%, rgba(${r},${g},${b},0.08) 0%, transparent 62%)`,
        }}
      />

      <div
        style={{
          position: "relative",
          zIndex: 1,
          width: "100%",
          maxWidth: 440,
          background: CARD_BG,
          border: "1px solid rgba(11,15,25,0.10)",
          borderRadius: 20,
          boxShadow:
            "0 40px 90px -45px rgba(11,15,25,0.34), 0 12px 30px -16px rgba(11,15,25,0.14)",
          padding: compact ? "32px 22px 30px" : "44px 44px 40px",
          boxSizing: "border-box",
        }}
      >
        {(isBrandedOrg || branding.logo_url) && (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
            }}
          >
            <PortalBrandLockup branding={branding} accent={accent} />
          </div>
        )}

        <div style={{ marginTop: compact ? 22 : isBrandedOrg ? 24 : 28 }}>
          {!isBrandedOrg && (
            <div
              style={{
                fontFamily:
                  "var(--font-inter), system-ui, -apple-system, 'Segoe UI', sans-serif",
                fontSize: 10,
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                color: "rgba(11,15,25,0.5)",
                textAlign: "center",
                marginBottom: 10,
              }}
            >
              {t("kundenportal")}
            </div>
          )}
          <h1
            style={{
              fontWeight: 700,
              fontSize: compact ? 22 : 24,
              letterSpacing: "-0.01em",
              color: INK,
              margin: 0,
              textAlign: "center",
            }}
          >
            {t("anmelden")}
          </h1>
          {!isBrandedOrg && (
            <p
              style={{
                fontSize: 14,
                lineHeight: 1.5,
                color: "rgba(11,15,25,0.55)",
                margin: "8px 0 0",
                textAlign: "center",
              }}
            >
              {t("loggePortal")}
            </p>
          )}

          <LoginForm
            compact={compact}
            accent={accent}
            accentText={accentText}
            accentShadow={accentShadow}
          />
        </div>
      </div>

      {nativeApp === "portal" ? (
        <div style={{ position: "relative", zIndex: 1, marginTop: 18 }}>
          <p
            style={{
              maxWidth: 360,
              margin: "0 auto 10px",
              fontSize: 13,
              lineHeight: 1.5,
              color: "rgba(11,15,25,0.55)",
              textAlign: "center",
            }}
          >
            {t("appZugangHinweis")}
          </p>
          <AppLegalLinks />
        </div>
      ) : (
        <p
          style={{
            position: "relative",
            zIndex: 1,
            marginTop: 22,
            fontSize: 12,
            color: "rgba(11,15,25,0.4)",
            textAlign: "center",
          }}
        >
          {t("bereitgestelltUeberHausverwalterAi")}
        </p>
      )}
    </div>
  );
}
